/**
 * Build a Fragrantica-style notes pyramid from product description HTML.
 * Expects store description format from catalog_copy:
 *   <p>blurb</p>
 *   <p><strong>Fragrance Notes</strong></p>
 *   <p><strong>Top Notes</strong><br/>Note · Note</p>
 *   <p><strong>Heart Notes</strong><br/>...</p>
 *   <p><strong>Base Notes</strong><br/>...</p>
 */
(() => {
  const NOTE_SPLIT = /[·•|,;/]+|\s+and\s+/i;

  function splitNotes(raw) {
    if (!raw) return [];
    return raw
      .split(NOTE_SPLIT)
      .map((n) => n.replace(/^[\s:\-–—]+|[\s:\-–—]+$/g, '').trim())
      .filter((n) => n && n.length > 1 && !/^notes?$/i.test(n));
  }

  function layerLabel(text) {
    const t = (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
    if (/top/.test(t)) return 'top';
    if (/heart|middle|mid/.test(t)) return 'heart';
    if (/base|bottom|dry/.test(t)) return 'base';
    if (/fragrance\s*notes?|notes?\s*pyramid|accords?/.test(t)) return 'header';
    if (/^size$/.test(t)) return 'size';
    return null;
  }

  function extractFromDescription(root) {
    const result = { intro: [], top: [], heart: [], base: [], size: null, other: [] };
    const paragraphs = Array.from(root.querySelectorAll('p, div, li'));

    if (!paragraphs.length) {
      // plain text fallback
      const text = root.textContent || '';
      parsePlainText(text, result);
      return result;
    }

    paragraphs.forEach((p) => {
      const strong = p.querySelector('strong, b');
      const full = (p.textContent || '').trim();
      if (!full) return;

      if (strong) {
        const label = layerLabel(strong.textContent);
        // Notes after the label (rest of paragraph)
        let notesText = full;
        if (strong.textContent) {
          notesText = full.replace(strong.textContent, '').replace(/^[\s:\-–—]+/, '').trim();
        }
        // Also try HTML after <br>
        const br = p.querySelector('br');
        if (br && br.nextSibling) {
          const after = Array.from(p.childNodes)
            .filter((n) => {
              // nodes after strong/br
              return true;
            });
          // Prefer text content after removing label
          notesText = full.replace(strong.textContent, '').replace(/^[\s:\-–—]+/, '').trim();
        }

        if (label === 'top') result.top = splitNotes(notesText);
        else if (label === 'heart') result.heart = splitNotes(notesText);
        else if (label === 'base') result.base = splitNotes(notesText);
        else if (label === 'header') {
          /* skip "Fragrance Notes" heading */
        } else if (label === 'size') {
          /* Skip Size block — variant picker already shows sizes */
        } else {
          result.intro.push(p.outerHTML);
        }
      } else {
        // Unlabeled paragraph = intro blurb (unless it looks like a notes line)
        if (/^(top|heart|middle|base)\s*notes?/i.test(full)) {
          parsePlainText(full, result);
        } else {
          result.intro.push(p.outerHTML);
        }
      }
    });

    return result;
  }

  function parsePlainText(text, result) {
    const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    lines.forEach((line) => {
      const m = line.match(/^(top|heart|middle|mid|base)\s*notes?\s*[:\-]?\s*(.+)$/i);
      if (m) {
        const kind = layerLabel(m[1] + ' notes');
        const notes = splitNotes(m[2]);
        if (kind === 'top') result.top = notes;
        if (kind === 'heart') result.heart = notes;
        if (kind === 'base') result.base = notes;
      }
    });
  }

  function chip(note) {
    const span = document.createElement('span');
    span.className = 'notes-pyramid__chip';
    span.textContent = note;
    return span;
  }

  function buildLayer(kind, label, notes) {
    if (!notes || !notes.length) return null;
    const layer = document.createElement('div');
    layer.className = `notes-pyramid__layer notes-pyramid__layer--${kind}`;
    const lab = document.createElement('p');
    lab.className = 'notes-pyramid__layer-label';
    lab.textContent = label;
    const chips = document.createElement('div');
    chips.className = 'notes-pyramid__chips';
    notes.forEach((n) => chips.appendChild(chip(n)));
    layer.appendChild(lab);
    layer.appendChild(chips);
    return layer;
  }

  function enhance(container) {
    if (!container || container.dataset.notesPyramid === 'done') return;
    const data = extractFromDescription(container);
    const hasPyramid = data.top.length || data.heart.length || data.base.length;
    if (!hasPyramid) return;

    const wrap = document.createElement('div');
    wrap.className = 'notes-pyramid';

    if (data.intro.length) {
      const intro = document.createElement('div');
      intro.className = 'notes-pyramid__intro';
      intro.innerHTML = data.intro.join('');
      wrap.appendChild(intro);
    }

    const title = document.createElement('p');
    title.className = 'notes-pyramid__title';
    title.textContent = 'Fragrance Notes';
    wrap.appendChild(title);

    const stack = document.createElement('div');
    stack.className = 'notes-pyramid__stack';

    const top = buildLayer('top', 'Top notes', data.top);
    const heart = buildLayer('heart', 'Heart notes', data.heart);
    const base = buildLayer('base', 'Base notes', data.base);
    [top, heart, base].forEach((el) => el && stack.appendChild(el));
    wrap.appendChild(stack);

    container.classList.add('product__description--has-pyramid');
    container.innerHTML = '';
    container.appendChild(wrap);
    container.dataset.notesPyramid = 'done';
  }

  function run() {
    document
      .querySelectorAll('[data-notes-pyramid], .product__description.rte')
      .forEach((el) => enhance(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Theme editor / section re-render
  document.addEventListener('shopify:section:load', run);
})();
